import React, { useEffect, useState } from "react";
import "../../styles/HowToCreateEvent.css";

/* =========================================================
   EVENT CREATION DOCUMENTATION IMAGES
   ========================================================= */

import createEventImage from "../../ui-assets/help/create-event/01-create-event.png";
import basicInfoImage from "../../ui-assets/help/create-event/02-basic-info.png";
import bannerLogoImage from "../../ui-assets/help/create-event/03-banner-logo.png";
import attendeeConfigImage from "../../ui-assets/help/create-event/04-attendee-registration.png";
import attendeeFormImage from "../../ui-assets/help/create-event/05-attendee-custom-form.png";
import speakerConfigImage from "../../ui-assets/help/create-event/06-speaker-registration.png";
import speakerFormImage from "../../ui-assets/help/create-event/07-speaker-custom-form.png";
import sponsorImage from "../../ui-assets/help/create-event/08-sponsor-registration.png";
import exhibitorImage from "../../ui-assets/help/create-event/09-exhibitors.png";
import previewImage from "../../ui-assets/help/create-event/10-preview.png";
import termsImage from "../../ui-assets/help/create-event/11-submit.png";
import unpublishedImage from "../../ui-assets/help/create-event/12-unpublish_event.png";
import publishImage from "../../ui-assets/help/create-event/13-confirm.png";
import Navbar from "../../components/Navbar";

/* =========================================================
   DOCUMENTATION DATA
   ========================================================= */

const documentationSteps = [
  {
    number: "01",
    title: "Open the Event Creation Page",
    icon: "fas fa-plus-circle",
    paragraphs: [
      <>From the EventFork home page, locate the <strong>Create Event</strong> button in the top navigation bar.</>,
      <>Click <strong>Create Event</strong> to open the event creation wizard.</>,
      <>The wizard is divided into three stages: <strong>Basic Info, Configuration, and Review.</strong></>,
    ],
    actionTitle: "What to do",
    actionText: "Click the Create Event button from the top navigation.",
    image: createEventImage,
    imageAlt: "EventFork home page showing the Create Event button",
    caption: "Figure 1. From the EventFork home page, click Create Event.",
    tip: "Make sure you are logged in before creating an event.",
  },
  {
    number: "02",
    title: "Enter Basic Event Information",
    icon: "fas fa-edit",
    paragraphs: [
      <>The first stage is <strong>Basic Info</strong>. Start by selecting the appropriate Event Type.</>,
      <>Choose the option that best matches your event, such as <strong>Conference, Meetup, Expo/Exhibition, or Event.</strong></>,
      <>Complete the required event details such as <strong>Event Name, Venue, City,</strong> and other available fields.</>,
      <>Fields marked with a red asterisk <strong>(*)</strong> are required before moving to the next stage.</>,
    ],
    actionTitle: "What to do",
    actionText: "Select the event type and complete all required event details.",
    image: basicInfoImage,
    imageAlt: "EventFork Basic Information page showing event type and required details",
    caption: "Figure 2. Select the event type and enter the required event information.",
  },
  {
    number: "03",
    title: "Add the Event Banner and Logo",
    icon: "fas fa-images",
    paragraphs: [
      <>Scroll down within the Basic Information page to the <strong>media section.</strong></>,
      <>Use <strong>Upload Banner</strong> to add the main event banner.</>,
      <>Use <strong>Upload Logo</strong> to add the event logo.</>,
      <>Check the supported image formats and maximum file size shown by the interface.</>,
      <>After confirming that both images are correct, click <strong>Next: Advanced Configuration.</strong></>,
    ],
    actionTitle: "What to do",
    actionText: "Upload a clear event banner and logo, then continue to Advanced Configuration.",
    image: bannerLogoImage,
    imageAlt: "EventFork page showing Upload Banner and Upload Logo controls",
    caption: "Figure 3. Upload the event banner and logo before continuing.",
    tip: "Use clear, high-quality images so the event looks consistent across EventFork pages.",
  },
  {
    number: "04",
    title: "Configure Attendee Registration",
    icon: "fas fa-users",
    paragraphs: [
      <>The <strong>Advanced Configuration</strong> page controls how participants can register for the event.</>,
      <>The <strong>Attendee</strong> section is shown first. Turn on <strong>Enable Attendee Registration</strong> when attendees should be able to register.</>,
      <>Set the <strong>Registration Start Date</strong> and <strong>Registration End Date.</strong></>,
      <>If required, specify an <strong>Attendee Limit.</strong></>,
      <>Select the Registration Approval method and choose between the default form and a <strong>Custom Form.</strong></>,
    ],
    actionTitle: "What to do",
    actionText: "Enable attendee registration and configure dates, limits, approval, and form type.",
    image: attendeeConfigImage,
    imageAlt: "EventFork Advanced Configuration attendee registration settings",
    caption: "Figure 4. Configure attendee registration settings.",
  },
  {
    number: "05",
    title: "Build a Custom Attendee Registration Form",
    icon: "fas fa-list-alt",
    paragraphs: [
      <>When <strong>Custom Form</strong> is selected, the Custom Form Builder opens.</>,
      <>Enter a <strong>Field Label</strong>, choose the <strong>Field Type,</strong> and optionally provide Placeholder Text.</>,
      <>Enable <strong>Required Field</strong> when attendees must provide that information.</>,
      <>Click <strong>Add Field</strong> to add the field.</>,
      <>The <strong>Live Preview</strong> panel shows how the attendee registration form will appear.</>,
      <>Review the fields and click <strong>Save Custom Form</strong> when ready.</>,
    ],
    actionTitle: "What to do",
    actionText: "Add only the attendee fields required for registration and save the custom form.",
    image: attendeeFormImage,
    imageAlt: "EventFork Custom Form Builder for attendee registration",
    caption: "Figure 5. Build the attendee custom registration form.",
    tip: "Keep custom forms concise. Only request information that is necessary for attendee registration or event management.",
  },
  {
    number: "06",
    title: "Configure Speaker Registration",
    icon: "fas fa-microphone-alt",
    paragraphs: [
      <>If the event will accept speaker applications, move to the <strong>Speaker</strong> section in Advanced Configuration.</>,
      <>Click <strong>Speaker</strong> in the participant configuration bar.</>,
      <>Turn on <strong>Enable Speaker Registration.</strong></>,
      <>Set the <strong>CFP Start Date</strong> and <strong>CFP End Date.</strong></>,
      <>Enter a <strong>Speaker Limit</strong> if the event has a maximum number of speakers.</>,
      <>Choose the required <strong>Speaker Form Type.</strong></>,
    ],
    actionTitle: "What to do",
    actionText: "Enable speaker registration and configure CFP dates, limits, and form type.",
    image: speakerConfigImage,
    imageAlt: "EventFork Advanced Configuration speaker registration settings",
    caption: "Figure 6. Configure speaker registration and CFP settings.",
  },
  {
    number: "07",
    title: "Complete Speaker Registration with a Custom Form",
    icon: "fas fa-microphone",
    paragraphs: [
      <>If Speaker Registration is enabled and <strong>Custom Form</strong> is selected, the Custom Form Builder opens.</>,
      <>Enter a <strong>Field Label</strong>, choose the <strong>Field Type,</strong> and add Placeholder Text when useful.</>,
      <>Enable <strong>Required Field</strong> for information that must be provided by the speaker.</>,
      <>Click <strong>Add Field</strong> for each additional field.</>,
      <>The <strong>Live Preview</strong> panel updates the speaker registration form in real time.</>,
      <>When the form is correct, click <strong>Save Custom Form.</strong></>,
    ],
    actionTitle: "What to do",
    actionText: "Create the speaker form, review the live preview, and save the custom form.",
    image: speakerFormImage,
    imageAlt: "EventFork Speaker Registration Custom Form Builder",
    caption: "Figure 7. Speaker Registration — Custom Form Builder and Live Preview.",
    tip: "Keep the speaker form focused on information genuinely needed for speaker selection and event management.",
  },
  {
    number: "08",
    title: "Configure Sponsor Registration",
    icon: "fas fa-handshake",
    paragraphs: [
      <>Move to the <strong>Sponsor</strong> section in the participant configuration bar.</>,
      <>Turn on <strong>Enable Sponsor Registration</strong> when the event will accept sponsors.</>,
      <>Under Sponsor Management, enter the <strong>Sponsor Name</strong> and select the <strong>Sponsor Category.</strong></>,
      <>Use <strong>Upload Logo</strong> to add the sponsor's logo.</>,
      <>Click <strong>Add Sponsor</strong> to save the entry.</>,
      <>Added sponsors appear in the Added Sponsors area.</>,
    ],
    actionTitle: "What to do",
    actionText: "Enable sponsor registration and add each sponsor with its name, category, and logo.",
    image: sponsorImage,
    imageAlt: "EventFork Sponsor Registration and Sponsor Management section",
    caption: "Figure 8. Sponsor Registration — Enable Registration and Add Sponsor.",
    tip: "Verify the sponsor name, category, and logo before adding each sponsor.",
  },
  {
    number: "09",
    title: "Configure Exhibitor Registration",
    icon: "fas fa-store",
    paragraphs: [
      <>Select the <strong>Exhibitor</strong> section in the participant configuration bar.</>,
      <>Turn on <strong>Enable Exhibitor Registration</strong> when the event will accept exhibitors.</>,
      <>In Exhibitor Management, enter the <strong>Exhibitor Name.</strong></>,
      <>Use <strong>Upload Logo</strong> to add the exhibitor logo.</>,
      <>Click <strong>Add Exhibitor</strong> to save the entry.</>,
      <>The <strong>Added Exhibitors</strong> area displays exhibitors already added.</>,
    ],
    actionTitle: "What to do",
    actionText: "Enable exhibitor registration and add each exhibitor with its name and logo.",
    image: exhibitorImage,
    imageAlt: "EventFork Exhibitor Registration and Exhibitor Management section",
    caption: "Figure 9. Exhibitor Registration — Enable Registration and Add Exhibitor.",
    tip: "Use clear exhibitor names and consistent logo files.",
  },
  {
    number: "10",
    title: "Review the Event in Preview & Submit",
    icon: "fas fa-search",
    paragraphs: [
      <>After completing the configuration, continue to <strong>Preview & Submit.</strong></>,
      <>This page gives you a consolidated view of the event before it is created.</>,
      <>Review the <strong>event name, event type, date and time, duration, location, pricing, registration status, limits, approval method, form types,</strong> and media.</>,
      <>The Registration Status panel shows which participant registrations are enabled and whether custom forms are used.</>,
      <>The Media panel confirms that the banner and logo have been uploaded.</>,
    ],
    actionTitle: "What to do",
    actionText: "Carefully review all event information, registration settings, and uploaded media.",
    image: previewImage,
    imageAlt: "EventFork Preview and Submit page showing event details and registration status",
    caption: "Figure 10. Preview & Submit — Review Event Details, Registration, and Media.",
  },
  {
    number: "11",
    title: "Review Terms and Create the Event",
    icon: "fas fa-check-circle",
    paragraphs: [
      <>Scroll further down the review page to the <strong>Terms & Conditions</strong> section.</>,
      <>Read the displayed terms and confirm that the required acceptance checkbox is selected.</>,
      <>If any information needs correction, use <strong>Back to Configuration.</strong></>,
      <>When all details are correct and the terms are accepted, click <strong>Create Event.</strong></>,
      <>The system then creates the event.</>,
    ],
    actionTitle: "What to do",
    actionText: "Accept the terms and conditions, verify the details, and click Create Event.",
    image: termsImage,
    imageAlt: "EventFork Terms and Conditions section with Create Event button",
    caption: "Figure 11. Terms & Conditions — Accept the Terms Before Creating the Event.",
  },
  {
    number: "12",
    title: "Find the Event in the Unpublished Events List",
    icon: "fas fa-folder-open",
    paragraphs: [
      <>After the event is created, open the <strong>EventFork Dashboard.</strong></>,
      <>Select the <strong>Unpublished</strong> tab.</>,
      <>The event cards show the event status, banner, event name, dates, location, event type, pricing, and management actions.</>,
      <>The action icons can be used to edit the event, delete it, publish it, or open sharing options.</>,
      <>Confirm that you are working with the correct event before selecting an action.</>,
    ],
    actionTitle: "What to do",
    actionText: "Open Dashboard → Unpublished and locate the newly created event.",
    image: unpublishedImage,
    imageAlt: "EventFork Dashboard showing unpublished events and event management actions",
    caption: "Figure 12. Dashboard — Unpublished Events and Event Management Actions.",
  },
  {
    number: "13",
    title: "Publish the Event",
    icon: "fas fa-paper-plane",
    paragraphs: [
      <>To make the event visible to the public, click the <strong>Publish</strong> icon on the required unpublished event card.</>,
      <>A confirmation dialog appears asking whether you want to publish the event.</>,
      <>Review the confirmation message.</>,
      <>Click <strong>Cancel</strong> if you still need to make changes.</>,
      <>If the event is ready for public visibility, click <strong>OK</strong> to confirm publication.</>,
      <>Once publication is confirmed, the event moves out of the unpublished workflow and becomes available according to its configured visibility and registration settings.</>,
    ],
    actionTitle: "What to do",
    actionText: "Click Publish on the correct event and confirm publication by clicking OK.",
    image: publishImage,
    imageAlt: "EventFork Publish Event confirmation dialog",
    caption: "Figure 13. Publish Confirmation — Confirm Publication with OK.",
  },
];

const TOTAL = documentationSteps.length;

/* =========================================================
   COMPONENT
   ========================================================= */

/**
 * Props (all optional):
 *  - helpUrl → link back to the Help Center. Default: "/help"
 *  - homeUrl → breadcrumb "Home" link. Default: "/"
 */
const HowToCreateEvent = ({ helpUrl = "/help", homeUrl = "/" }) => {
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(documentationSteps[0].number);
  const [zoom, setZoom] = useState(null);

  /* Reading progress */
  useEffect(() => {
    const onScroll = () => {
      const max =
        document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Track which step is in view (drives the side rail) */
  useEffect(() => {
    const steps = document.querySelectorAll(".doc-step");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.dataset.step);
          }
        });
      },
      { rootMargin: "-20% 0px -65% 0px" }
    );
    steps.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* Close the screenshot viewer with Esc */
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zoom]);

  const activeIndex = documentationSteps.findIndex(
    (s) => s.number === active
  );

  return (
    
    <div className="doc-page">
      <div
        className="doc-progress"
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      />

      {/* ── Compact header ── */}
      <header className="doc-hero">
        <div className="doc-hero__inner">
          <div className="doc-hero__text">
            <nav className="doc-breadcrumb" aria-label="Breadcrumb">
              <a href={homeUrl}>Home</a>
              <span aria-hidden="true">/</span>
              <a href={helpUrl}>Help Center</a>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Create event</span>
            </nav>
            <h1>How to Create an Event</h1>
            <p>
              Create, configure, review and publish your event on EventFork.
            </p>
          </div>

          <div className="doc-hero__meta">
            <span>
              <i className="fas fa-layer-group" aria-hidden="true" />
              {TOTAL} steps
            </span>
            <span>
              <i className="fas fa-images" aria-hidden="true" />
              {TOTAL} screenshots
            </span>
          </div>
        </div>
      </header>

      <div className="doc-layout">
        {/* ── Side rail: the route through the 13 steps ── */}
        <aside className="doc-rail" aria-label="Steps">
          <div className="doc-rail__status">
            <strong>
              Step {activeIndex + 1} <span>of {TOTAL}</span>
            </strong>
            <div className="doc-rail__bar" aria-hidden="true">
              <span
                style={{
                  width: `${((activeIndex + 1) / TOTAL) * 100}%`,
                }}
              />
            </div>
          </div>

          <ol className="doc-rail__list">
            {documentationSteps.map((step) => (
              <li key={step.number}>
                <a
                  href={`#step-${step.number}`}
                  className={step.number === active ? "is-active" : ""}
                  aria-current={step.number === active ? "step" : undefined}
                >
                  <span className="doc-rail__num">{step.number}</span>
                  <span className="doc-rail__title">{step.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </aside>

        <main className="doc-main">
          {/* Mobile step list */}
          <details className="doc-toc-mobile">
            <summary>
              <i className="fas fa-list" aria-hidden="true" /> All steps
              <span>
                {activeIndex + 1}/{TOTAL}
              </span>
            </summary>
            <ol>
              {documentationSteps.map((step) => (
                <li key={step.number}>
                  <a href={`#step-${step.number}`}>
                    <b>{step.number}</b> {step.title}
                  </a>
                </li>
              ))}
            </ol>
          </details>

          {/* ── Steps ── */}
          {documentationSteps.map((step) => (
            <section
              className="doc-step"
              id={`step-${step.number}`}
              data-step={step.number}
              key={step.number}
            >
              <div className="doc-step__head">
                <span className="doc-step__num">{step.number}</span>
                <h2>{step.title}</h2>
                <i className={step.icon} aria-hidden="true" />
              </div>

              <div className="doc-step__body">
                <div className="doc-step__text">
                  {step.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                <div className="doc-step__side">
                  <div className="doc-do">
                    <strong>
                      <i className="fas fa-mouse-pointer" aria-hidden="true" />
                      {step.actionTitle}
                    </strong>
                    <span>{step.actionText}</span>
                  </div>

                  {step.tip && (
                    <div className="doc-tip">
                      <strong>
                        <i className="fas fa-lightbulb" aria-hidden="true" />
                        Tip
                      </strong>
                      <span>{step.tip}</span>
                    </div>
                  )}
                </div>
              </div>

              <figure className="doc-figure">
                <div className="doc-frame">
                  <div className="doc-frame__bar">
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                    <span>Step {step.number} screenshot</span>
                    <button
                      type="button"
                      onClick={() => setZoom(step)}
                      aria-label={`Enlarge screenshot for step ${step.number}`}
                    >
                      <i className="fas fa-expand-alt" aria-hidden="true" />
                    </button>
                  </div>
                  <img
                    src={step.image}
                    alt={step.imageAlt}
                    loading="lazy"
                    onClick={() => setZoom(step)}
                  />
                </div>
                <figcaption>{step.caption}</figcaption>
              </figure>
            </section>
          ))}

          {/* ── Completion ── */}
          <section className="doc-done">
            <div className="doc-done__icon">
              <i className="fas fa-check" aria-hidden="true" />
            </div>
            <div className="doc-done__text">
              <h2>Your event is ready</h2>
              <p>
                You have created, configured, reviewed and published your
                event.
              </p>
              <ul>
                <li>Basic information</li>
                <li>Registration</li>
                <li>Sponsors &amp; exhibitors</li>
                <li>Review &amp; terms</li>
                <li>Publication</li>
              </ul>
            </div>
            <a href={helpUrl}>Back to Help Center</a>
          </section>
        </main>
      </div>

      {/* ── Screenshot viewer ── */}
      {zoom && (
        <div
          className="doc-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Step ${zoom.number} screenshot`}
          onClick={() => setZoom(null)}
        >
          <button
            type="button"
            className="doc-lightbox__close"
            onClick={() => setZoom(null)}
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
          <img
            src={zoom.image}
            alt={zoom.imageAlt}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HowToCreateEvent;